import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@theme/useTheme';
import { useAuth } from '@contexts/AuthContext';
import { apiService } from '@services/api';
import GradientBackground from '@components/ui/GradientBackground';
import { FaCalendarAlt, FaChartBar, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaChevronDown, FaChevronUp, FaWeight, FaHeartbeat, FaBed, FaRunning, FaHistory, FaTimes } from 'react-icons/fa';
import Spinner from '@components/ui/Spinner';
import { TrackedLink } from '@components/analytics/TrackedLink';
import { trackGtmButtonClick } from '@/analytics/gtm';
import type { AssessmentHistoryItem } from '../types/form.types';

const AssessmentHistory: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated, userToken } = useAuth();
  const [assessments, setAssessments] = useState<AssessmentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentHistoryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchAssessmentHistory = async () => {
      if (!isAuthenticated || !userToken) {
        setError('Please log in to view your assessment history');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await apiService.getAssessmentHistory();
        if (response.has_assessments && response.assessments) {
          setAssessments(response.assessments as AssessmentHistoryItem[]);
        } else {
          setAssessments([]);
        }
      } catch (err) {
        console.error('Failed to fetch assessment history:', err);
        setError(err instanceof Error ? err.message : 'Failed to load assessment history');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessmentHistory();
  }, [isAuthenticated, userToken]);


  useEffect(()=>{
    if(isModalOpen)
    {
      document.body.style.overflow = 'hidden';
    }
    else 
    {
      document.body.style.overflow = 'auto';
    }
    return ()=>{
      document.body.style.overflow = 'auto';
    }
  },[isModalOpen])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openModal = (assessment: AssessmentHistoryItem) => {
    setSelectedAssessment(assessment);
    setIsModalOpen(true);
    setExpandedSections({}); // Reset expanded sections when opening modal
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAssessment(null);
    setExpandedSections({});
  };


  const getSleepStageText = (stage: number) => {
    switch (stage) {
      case 1: return 'N1 Light Sleep';
      case 2: return 'N2 Moderate Sleep';
      case 3: return 'N3 Deep Sleep';
      case 4: return 'REM Sleep';
      default: return 'Unknown';
    }
  };

  const getSleepPositionText = (position: number) => {
    switch (position) {
      case 1: return 'Supine (Back)';
      case 2: return 'Side';
      default: return 'Unknown';
    }
  };

  const getSmokingStatusText = (status: number) => {
    switch (status) {
      case 1: return 'Never Smoked';
      case 2: return 'Former Smoker';
      case 3: return 'Current Smoker';
      default: return 'Unknown';
    }
  };

  const getAlcoholConsumptionText = (consumption: number) => {
    switch (consumption) {
      case 1: return 'None';
      case 2: return 'Light (1-2/week)';
      case 3: return 'Moderate (3-7/week)';
      case 4: return 'Heavy (8+/week)';
      default: return 'Unknown';
    }
  };

  const getPhysicalActivityText = (activity: number) => {
    switch (activity) {
      case 1: return 'Sedentary';
      case 2: return 'Light Activity';
      case 3: return 'Moderate Activity';
      case 4: return 'High Activity';
      default: return 'Unknown';
    }
  };

  const getThyroidFunctionText = (function_: number) => {
    switch (function_) {
      case 1: return 'Normal';
      case 2: return 'Hypothyroidism';
      case 3: return 'Hyperthyroidism';
      default: return 'Unknown';
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high':
        return {
          text: colors.error,
          background: colors.error + '20',
          border: colors.error + '40'
        };
      case 'moderate':
        return {
          text: '#f59e0b',
          background: '#fef3c7',
          border: '#fde68a'
        };
      case 'low':
        return {
          text: '#10b981',
          background: '#d1fae5',
          border: '#a7f3d0'
        };
      default:
        return {
          text: colors.text,
          background: colors.input,
          border: colors.border
        };
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high':
        return <FaExclamationTriangle className="w-5 h-5" />;
      case 'moderate':
        return <FaInfoCircle className="w-5 h-5" />;
      case 'low':
        return <FaCheckCircle className="w-5 h-5" />;
      default:
        return <FaChartBar className="w-5 h-5" />;
    }
  };

  if (!isAuthenticated) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary + '20' }}>
            <FaExclamationTriangle className="w-8 h-8" style={{ color: colors.primary }} />
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>
            Authentication Required
          </h2>
          <p className="text-lg mb-6" style={{ color: colors.text }}>
            Please log in to view your assessment history.
          </p>
          <button
            onClick={() => {
              trackGtmButtonClick({
                button_id: 'history_auth_required_go_assessment',
                section: 'page_assessment_history',
                label: 'Go to Assessment',
                destination: '/assessment',
              });
              window.location.href = '/assessment';
            }}
            className="px-6 py-3 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: colors.primary,
              color: 'white'
            }}
          >
            Go to Assessment
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <div className="text-center">
          <Spinner size="xl" color={colors.primary} className="mb-4" />
          <p style={{ color: colors.text }}>Loading assessment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      {/* Hero Section */}
      <div className="relative pt-32 pb-8 sm:pt-40 sm:pb-12 lg:pt-48 lg:pb-16 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
        {/* Gradient background */}
        <GradientBackground position="top" height="large" />

        <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                style={{ color: colors.heading }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                Assessment History
              </motion.h1>
              <motion.p
                className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                style={{ color: colors.text }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                View your past sleep apnea assessments and track your progress over time
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-center">
                <Spinner size="xl" color={colors.primary} className="mb-4" />
                <p style={{ color: colors.text }}>
                  Loading assessment history...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <div
                className="p-6 rounded-lg border-2 mx-auto max-w-md"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.error,
                  color: colors.error
                }}
              >
                <FaExclamationTriangle className="text-3xl mx-auto mb-4" />
                <p className="text-lg font-semibold mb-2">Unable to Load History</p>
                <p className="text-sm mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-md"
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white'
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && assessments.length === 0 && (
            <div className="text-center py-8">
              <div
                className="p-6 rounded-lg border-2 mx-auto max-w-md"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.primary,
                  color: colors.primary
                }}
              >
                <FaHistory className="text-3xl mx-auto mb-4" />
                <p className="text-lg font-semibold mb-2">No Assessments Yet</p>
                <p className="text-sm mb-4">Complete your first assessment to start tracking your sleep health.</p>
                <button
                  onClick={() => window.location.href = '/assessment'}
                  className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-md"
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white'
                  }}
                >
                  Take Assessment
                </button>
              </div>
            </div>
          )}

          {/* Assessment History */}
          {!isLoading && !error && assessments.length > 0 && (
            <section className="mt-4 mb-8 sm:mb-12">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold mb-6" style={{ color: colors.heading }}>
                  Your Assessments ({assessments.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {assessments.map((assessment, index) => (
                    <motion.article
                      key={assessment.id || `assessment-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="rounded-tr-3xl rounded-bl-3xl border border-black overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                      style={{ backgroundColor: colors.background }}
                    >
                      {/* Assessment Header */}
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="p-2 rounded-lg border"
                              style={{
                                color: getRiskLevelColor(assessment.risk_level).text,
                                backgroundColor: getRiskLevelColor(assessment.risk_level).background,
                                borderColor: getRiskLevelColor(assessment.risk_level).border
                              }}
                            >
                              {getRiskLevelIcon(assessment.risk_level)}
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg" style={{ color: colors.heading }}>
                                Assessment #{assessments.length - index}
                              </h3>
                              <p className="text-sm" style={{ color: colors.text }}>
                                <FaCalendarAlt className="w-4 h-4 inline mr-1" />
                                {formatDate(assessment.taken_at)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div
                              className="px-3 py-1 rounded-full text-sm font-medium border"
                              style={{
                                color: getRiskLevelColor(assessment.risk_level).text,
                                backgroundColor: getRiskLevelColor(assessment.risk_level).background,
                                borderColor: getRiskLevelColor(assessment.risk_level).border
                              }}
                            >
                              {assessment.risk_level} Risk
                            </div>
                            <div className="text-2xl font-bold mt-1" style={{ color: colors.heading }}>
                              {assessment.risk_score}%
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                          <div>
                            <span className="font-medium" style={{ color: colors.text }}>BMI:</span>
                            <span className="ml-1" style={{ color: colors.text }}>{assessment.bmi}</span>
                          </div>
                          <div>
                            <span className="font-medium" style={{ color: colors.text }}>Weight:</span>
                            <span className="ml-1" style={{ color: colors.text }}>{assessment.weight} lbs</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              trackGtmButtonClick({
                                button_id: 'history_card_view_details',
                                section: 'page_assessment_history',
                                label: 'View Details',
                                content_id: assessment.id,
                              });
                              openModal(assessment);
                            }}
                            className="flex-1 flex items-center justify-center px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-md"
                            style={{
                              backgroundColor: colors.primary,
                              color: 'white'
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </div>

            </section>
          )}

          {/* Call to Action */}
          <section className="text-center mb-8 sm:mb-12">
            <div
              className="p-8 lg:p-12 rounded-tr-3xl rounded-bl-3xl border border-black"
              style={{ backgroundColor: colors.background }}
            >
              <h2
                className="text-3xl sm:text-4xl font-bold mb-6"
                style={{ color: colors.heading }}
              >
                Track Your Sleep Health Progress
              </h2>
              <p
                className="text-lg mb-8 max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                Regular assessments help you monitor changes in your sleep health and track the effectiveness of lifestyle modifications or treatments.
              </p>
              <TrackedLink
                to="/assessment"
                className="px-8 py-4 rounded-full font-semibold text-white text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                style={{ backgroundColor: colors.primary }}
                gtmId="history_cta_new_assessment"
                gtmSection="page_assessment_history"
                gtmLabel="Take New Assessment"
              >
                Take New Assessment
              </TrackedLink>
            </div>
          </section>
        </div>
      </main>

      {/* Modal */}
      {isModalOpen && selectedAssessment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Glass Backdrop */}
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{ backgroundColor: colors.background + '20' }}
            onClick={closeModal}
          ></div>

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-tr-3xl rounded-bl-3xl border shadow-2xl"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border
            }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: colors.input }}>
              <h3 className="text-2xl font-bold" style={{ color: colors.heading }}>
                Assessment Details
              </h3>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg transition-colors hover:opacity-80"
                style={{ backgroundColor: colors.error + '20', borderColor: colors.error }}
                aria-label="Close modal"
                title="Close modal"
              >
                <FaTimes style={{ color: colors.error }} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Risk Score */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium" style={{ color: colors.text }}>Risk Score</span>
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium border"
                    style={{
                      color: getRiskLevelColor(selectedAssessment.risk_level).text,
                      backgroundColor: getRiskLevelColor(selectedAssessment.risk_level).background,
                      borderColor: getRiskLevelColor(selectedAssessment.risk_level).border
                    }}
                  >
                    {selectedAssessment.risk_level} Risk
                  </span>
                </div>
                <div
                  className="w-full rounded-full h-3 mb-2"
                  style={{ backgroundColor: colors.input }}
                >
                  <div
                    className="h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${selectedAssessment.risk_score}%`,
                      backgroundColor: selectedAssessment.risk_level.toLowerCase() === 'high' ? '#ef4444' :
                        selectedAssessment.risk_level.toLowerCase() === 'moderate' ? '#f59e0b' : '#10b981'
                    }}
                  ></div>
                </div>
                <p className="text-2xl font-bold" style={{ color: colors.heading }}>
                  {selectedAssessment.risk_score}%
                </p>
              </div>

              {/* Physical Measurements */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('physical')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaWeight style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Physical Measurements</span>
                  </div>
                  {expandedSections.physical ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.physical && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                      <span className="font-medium" style={{ color: colors.text }}>Height:</span>
                      <span className="ml-1" style={{ color: colors.text }}>
                        {selectedAssessment.height_feet !== undefined && 
                        selectedAssessment.height_feet !== null &&
                        selectedAssessment.height_inch !== undefined && 
                        selectedAssessment.height_inch !== null
                          ? `${selectedAssessment.height_feet}' ${selectedAssessment.height_inch}"`
                            : selectedAssessment.height 
                              ? `${selectedAssessment.height} ft` 
                              : 'N/A'
                        }
                          </span>
                        </div>

                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Weight:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.weight} lbs</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>BMI:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.bmi}</span>
                      </div>
                      {selectedAssessment.neck_circumference && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Neck:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.neck_circumference} inches</span>
                        </div>
                      )}
                      {selectedAssessment.waist_circumference && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Waist:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.waist_circumference} inches</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sleep Study Results */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('sleepStudy')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaChartBar style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Sleep Study Results</span>
                  </div>
                  {expandedSections.sleepStudy ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.sleepStudy && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedAssessment.tonsil_size && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Tonsil Size:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.tonsil_size}/5</span>
                        </div>
                      )}
                      {selectedAssessment.ahi && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>AHI:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.ahi}</span>
                        </div>
                      )}
                      {selectedAssessment.odi && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>ODI:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.odi}</span>
                        </div>
                      )}
                      {selectedAssessment.rei && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>REI:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.rei}</span>
                        </div>
                      )}
                      {selectedAssessment.plmi && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>PLMI:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.plmi}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sleep Parameters */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('sleepParams')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaBed style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Sleep Parameters</span>
                  </div>
                  {expandedSections.sleepParams ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.sleepParams && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Sleep Stage:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{getSleepStageText(selectedAssessment.sleep_stage)}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Sleep Position:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{getSleepPositionText(selectedAssessment.sleep_position)}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Sleep Duration:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.sleep_duration} hours</span>
                      </div>
                      {selectedAssessment.mean_heart_rate && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Heart Rate:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.mean_heart_rate} bpm</span>
                        </div>
                      )}
                      {selectedAssessment.oxygen_saturation && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Oxygen Sat:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.oxygen_saturation}%</span>
                        </div>
                      )}
                      {selectedAssessment.arousal_index && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Arousal Index:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.arousal_index}</span>
                        </div>
                      )}
                      {selectedAssessment.respiratory_disturbance_index && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>RDI:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.respiratory_disturbance_index}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Symptoms */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('symptoms')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaHeartbeat style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Symptoms</span>
                  </div>
                  {expandedSections.symptoms ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.symptoms && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Snoring:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.snoring || 'Not specified'}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Witnessed Apnea:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.witnessed_apnea || 'Not specified'}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Breathing Stops:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.breathing_stops}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Morning Headaches:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.morning_headaches}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Daytime Fatigue:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.daytime_fatigue}/10</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Morning Dry Mouth:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.morning_dry_mouth}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Nocturia:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.nocturia} times/night</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Medical History */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('medical')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaInfoCircle style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Medical History</span>
                  </div>
                  {expandedSections.medical ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.medical && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Hypertension:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.hypertension}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Diabetes:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.diabetes}</span>
                      </div>
                      {selectedAssessment.thyroid_function && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Thyroid:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{getThyroidFunctionText(selectedAssessment.thyroid_function)}</span>
                        </div>
                      )}
                      {selectedAssessment.family_history && (
                        <div>
                          <span className="font-medium" style={{ color: colors.text }}>Family History:</span>
                          <span className="ml-1" style={{ color: colors.text }}>{selectedAssessment.family_history}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Lifestyle Factors */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('lifestyle')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaRunning style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Lifestyle Factors</span>
                  </div>
                  {expandedSections.lifestyle ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.lifestyle && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Smoking:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{getSmokingStatusText(selectedAssessment.smoking_status)}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Alcohol:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{getAlcoholConsumptionText(selectedAssessment.alcohol_consumption)}</span>
                      </div>
                      <div>
                        <span className="font-medium" style={{ color: colors.text }}>Physical Activity:</span>
                        <span className="ml-1" style={{ color: colors.text }}>{getPhysicalActivityText(selectedAssessment.physical_activity)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Risk Factors */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('riskFactors')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaExclamationTriangle style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Risk Factors ({selectedAssessment.risk_factors?.length || 0})</span>
                  </div>
                  {expandedSections.riskFactors ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.riskFactors && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="space-y-2">
                      {(selectedAssessment.risk_factors || []).map((factor, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                          <span className="text-sm" style={{ color: colors.text }}>{factor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Recommendations */}
              <div className="mb-6">
                <button
                  onClick={() => toggleSection('recommendations')}
                  className="flex items-center justify-between w-full p-3 rounded-lg border transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.input, borderColor: colors.input }}
                >
                  <div className="flex items-center gap-2">
                    <FaCheckCircle style={{ color: colors.primary }} />
                    <span className="font-semibold" style={{ color: colors.heading }}>Recommendations ({selectedAssessment.recommendations?.length || 0})</span>
                  </div>
                  {expandedSections.recommendations ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.recommendations && (
                  <div className="mt-3 p-4 rounded-lg border" style={{ backgroundColor: colors.background, borderColor: colors.input }}>
                    <div className="space-y-2">
                      {(selectedAssessment.recommendations || []).map((recommendation, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: colors.primaryButton }}></div>
                          <span className="text-sm" style={{ color: colors.text }}>{recommendation}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Assessment Date */}
              <div className="pt-4 border-t" style={{ borderColor: colors.input }}>
                <p className="text-sm" style={{ color: colors.text }}>
                  <FaCalendarAlt className="w-4 h-4 inline mr-1" />
                  Completed on {formatDate(selectedAssessment.taken_at)}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}


    </div>
  );
};

export default AssessmentHistory;
